const PRIVACY_KEY_PREFIX = "thinccc.facilitator.names-concealed";

/**
 * SHARED-MACHINE PRIVACY, and the default is NAMES VISIBLE (Amir, 2026-08-09).
 *
 * The switch used to default to concealed, so every facilitator opening the OS
 * for the first time met a room of placeholders and had to find a control to
 * see the students they teach. That is the wrong opening position for a
 * single-facilitator laptop, which is the ordinary case; the shared-machine
 * case is the one that needs a deliberate act, and it has one. Concealment is
 * now something you choose, and once chosen it is honoured everywhere.
 *
 * ABSENCE AND UNREADABILITY ARE NOT THE SAME ANSWER, and this is the whole of
 * the read's logic. "Nothing is stored" means nobody has chosen yet, so the new
 * default applies and names show. "Something is stored and I cannot read it"
 * means a choice EXISTS and its content is unknown, so the read fails
 * concealed: a malformed value, a throwing `getItem` and an unavailable
 * `sessionStorage` all return true rather than guessing that the missing choice
 * was the permissive one. A flipped default must not become a mechanism for
 * revealing names against an explicit instruction that merely could not be read.
 *
 * THE IN-PROCESS MAP IS AUTHORITATIVE OVER STORAGE, and the order matters more
 * than the Map does. `sessionStorage.setItem` can refuse (quota, a hardened
 * profile, private-mode policy). If a facilitator switches to concealed and the
 * write refuses, storage keeps whatever it had. Consulting storage first would
 * let a stale, perfectly VALID "off" beat the concealment they just asked for
 * the moment this provider remounts, which is the failure the fallback exists
 * to prevent. So a value recorded in this process wins outright, and storage is
 * only consulted when this process has recorded nothing for that identity.
 *
 * ACCEPTED RESIDUAL, recorded rather than papered over: a FULL PAGE RELOAD
 * after a failed `setItem` of "on" loses the choice and lands on names visible.
 * The Map dies with the JavaScript context and there is nowhere else to put the
 * choice on a client whose storage is refusing writes. It is unclosable from
 * here, and the sequence that reaches it is a storage failure the facilitator
 * can see the consequence of and repeat the toggle against.
 */
const processChoices = new Map();

const identity = (userId) => String(userId ?? "").trim() || null;

const storageKey = (userId) => {
  const id = identity(userId);
  return id ? `${PRIVACY_KEY_PREFIX}:${id}` : null;
};

const getSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

export const readNamesConcealed = (userId) => {
  const id = identity(userId);
  // PRE-IDENTITY, and it is deliberately the permissive answer. There is no
  // per-user choice to honour before an identity exists, and the provider does
  // not paint a real name off this value anyway: its own transition guard holds
  // every render concealed until the identity it is rendering for has settled.
  if (!id) return false;

  if (processChoices.has(id)) return processChoices.get(id);

  const storage = getSessionStorage();
  if (!storage) return true;

  let value;
  try {
    value = storage.getItem(storageKey(id));
  } catch (error) {
    return true;
  }

  if (value === null || value === undefined) return false;
  if (value === "off") return false;
  return true;
};

export const writeNamesConcealed = (userId, concealed) => {
  const id = identity(userId);
  const next = concealed !== false;
  // RECORDED IN PROCESS FIRST, unconditionally, because this line is the only
  // one guaranteed to run. Everything below it can refuse.
  if (id) processChoices.set(id, next);

  const key = storageKey(userId);
  const storage = getSessionStorage();
  if (!key || !storage) return;

  try {
    storage.setItem(key, next ? "on" : "off");
  } catch (error) {
    // The choice still holds for this JavaScript context through the Map above.
    // See the accepted residual in this file's header for what a full reload
    // costs when storage refuses.
  }
};

export const clearNamesConcealed = (userId) => {
  const id = identity(userId);
  // BEFORE THE STORAGE EARLY RETURN, not after it. Sign-out has to drop the
  // previous facilitator's choice even on a machine where storage is
  // unavailable, and every early return below this line would skip it.
  if (id) processChoices.delete(id);

  const key = storageKey(userId);
  const storage = getSessionStorage();
  if (!key || !storage) return;

  try {
    storage.removeItem(key);
  } catch (error) {
    // A storage refusal does not block signout.
  }
};
